import type { EagleParam } from '../api/types'
import { t } from '../i18n/lingua'

/**
 * I testi di Eagle composti dalla console a partire dai codici del Control
 * Plane (`kind`, `name`, `reason_code`, `method`): così si traducono.
 */

/** Un numero come lo mostra Eagle: interi sopra 100, un decimale sotto. */
export function formatValore(v: number): string {
  return Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1).replace(/\.0$/, '')
}

/**
 * The display scale of a unit for the largest value it has to show: Mbit/s
 * grows to Gbit/s and shrinks to kbit/s, ms to s, min to h; every other unit
 * stays as it is. One scale per axis, so all the ticks of a chart share the
 * same unit and «1.5 Gbit/s» never sits next to «800 Mbit/s».
 */
export function scalaUnita(unit: string, massimo: number): { fattore: number; unit: string } {
  if (unit === 'Mbit/s') {
    if (massimo >= 1000) return { fattore: 1000, unit: 'Gbit/s' }
    if (massimo > 0 && massimo < 1) return { fattore: 0.001, unit: 'kbit/s' }
  }
  if (unit === 'ms' && massimo >= 1000) return { fattore: 1000, unit: 's' }
  if (unit === 'pkt/s') {
    if (massimo >= 1_000_000) return { fattore: 1_000_000, unit: 'Mpkt/s' }
    if (massimo >= 1000) return { fattore: 1000, unit: 'kpkt/s' }
  }
  if (unit === 'min' && massimo >= 120) return { fattore: 60, unit: 'h' }
  return { fattore: 1, unit }
}

/**
 * A tick label: up to two decimals below 10, one below 100, none above, with
 * the trailing zeros dropped — «0.25 Gbit/s» must not become «0.3».
 */
export function formatPreciso(v: number): string {
  const a = Math.abs(v)
  const s = a >= 100 ? v.toFixed(0) : a >= 10 ? v.toFixed(1) : v.toFixed(2)
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s
}

/** A value with its unit, scaled on its own: for a single number (a row, a pill, a tooltip). */
export function formatConUnita(v: number | null | undefined, unit: string | null | undefined): string {
  if (v == null) return '—'
  const s = scalaUnita(unit ?? '', Math.abs(v))
  const n = formatValore(v / s.fattore)
  return s.unit ? `${n} ${s.unit}` : n
}

type ConEtichetta = Pick<EagleParam, 'kind' | 'name' | 'key'>

export function etichettaParametro(p: ConEtichetta): string {
  switch (p.kind) {
    case 'ping':
      return t('Raggiungibilità (ping)')
    case 'ping_fast':
      return t('Ping rapido (latenza)')
    case 'tcp':
      return t('Servizio {porta}/tcp', { porta: p.name })
    case 'uptime':
      return t('Tempo dall’avvio')
    case 'cpu':
      return t('CPU')
    case 'memory':
      return t('Memoria')
    case 'volume':
      return t('Volume {mount}', { mount: p.name })
    case 'disk_health':
      return t('Salute del disco {nome}', { nome: p.name })
    case 'updates_pending':
      return t('Aggiornamenti in sospeso')
    case 'reboot_required':
      return t('Riavvio richiesto')
    case 'services_auto':
      return t('Servizi automatici')
    case 'service':
      return t('Servizio {nome}', { nome: p.name })
    case 'service_group':
      return t('Gruppo di servizi «{nome}»', { nome: p.name })
    case 'process':
      return t('Processo {nome}', { nome: p.name })
    case 'interface':
      return p.name
    case 'ups_battery':
      return t('Carica della batteria')
    case 'ups_runtime':
      return t('Autonomia')
    case 'ups_load':
      return t('Carico')
    default:
      return p.name || p.key
  }
}

export function cosaMisura(p: EagleParam): string {
  switch (p.kind) {
    case 'ping':
      return t('perdita di pacchetti')
    case 'ping_fast':
      return t('latenza misurata ogni {s} secondi e riassunta al minuto (media, minimo, massimo, pacchetti persi): le fluttuazioni che un ping al minuto non vede', { s: p.extra.every_s ?? 5 })
    case 'tcp':
      return t('tempo di risposta')
    case 'uptime':
      return t('ore dall’avvio: sotto la soglia la macchina è ripartita da poco')
    case 'cpu':
    case 'memory':
      return t('utilizzo')
    case 'volume':
      return t('spazio usato')
    case 'disk_health':
      return t('guasto previsto (SMART); sulle macchine virtuali non è disponibile')
    case 'updates_pending':
      return t('dall’ultima ricerca del sistema, senza andare in rete')
    case 'reboot_required':
      return t('il sistema chiede un riavvio per completare gli aggiornamenti')
    case 'interface':
      return t('utilizzo di banda, errori e stato')
    case 'services_auto':
      return t('quanti servizi ad avvio automatico sono fermi (su Linux: unità in errore)')
    case 'service':
      return t('in esecuzione')
    case 'service_group': {
      const regola = [
        ...(p.extra.services ?? []),
        ...(p.extra.pattern ? [p.extra.pattern] : []),
      ].join(', ')
      return p.extra.min
        ? t('almeno {n} attivi fra: {regola}', { n: p.extra.min, regola })
        : t('tutti attivi: {regola}', { regola })
    }
    case 'process':
      return t('istanze in esecuzione')
    default:
      return ''
  }
}

export function etichettaMetodo(p: EagleParam): string {
  switch (p.method) {
    case 'icmp':
      return t('Ping dalla sonda')
    case 'tcp':
      return t('Connessione TCP dalla sonda')
    case 'snmp':
      return 'SNMP'
    case 'ssh':
      return 'SSH'
    case 'winrm':
      return 'WinRM'
    case 'agent':
      return t('Agente d’inventario')
    case 'api':
      return t('API {piattaforma}', { piattaforma: p.extra.platform_label ?? p.extra.platform ?? '' })
    default:
      return '—'
  }
}

export function motivo(p: EagleParam): string {
  switch (p.reason_code) {
    case 'no_source':
      return t('Nessuna fonte dell’inventario legge questo dato su questo CI: aggiungi una credenziale SNMP, SSH o WinRM, oppure installa l’agente d’inventario.')
    case 'no_address':
      return t('Il CI non ha un indirizzo da interrogare.')
    case 'no_credential':
      return t('Nessuna credenziale SNMP nel tuo spazio: aggiungine una in Credenziali e Eagle la proverà al primo giro.')
    case 'credential_probe':
      return t('La discovery non ha ancora detto quale credenziale SNMP risponde: Eagle prova quelle del tuo spazio al primo giro e ricorda la buona.')
    case 'method_pending':
      return t('La lettura via {metodo} arriva con un prossimo aggiornamento: puoi già sceglierlo, la scelta resta salvata.', { metodo: etichettaMetodo(p) })
    case 'no_longer':
      return t('Non più in inventario: la scelta resta finché non la togli.')
    default:
      return ''
  }
}

export const GRUPPI: { key: EagleParam['group']; label: () => string }[] = [
  { key: 'disponibilita', label: () => t('Disponibilità') },
  { key: 'risorse', label: () => t('Risorse') },
  { key: 'sistema', label: () => t('Sistema') },
  { key: 'servizi', label: () => t('Servizi') },
  { key: 'processi', label: () => t('Processi') },
  { key: 'interfacce', label: () => t('Interfacce') },
  { key: 'classe', label: () => t('Specifici della classe') },
  { key: 'non_piu', label: () => t('Non più in inventario') },
]

export function etichettaFrequenza(secondi: number): string {
  if (secondi < 3600) return t('ogni {n} min', { n: Math.round(secondi / 60) })
  return t('ogni {n} h', { n: Math.round(secondi / 3600) })
}

export function profilo(key: string): { label: string; descrizione: string } {
  switch (key) {
    case 'rete':
      return {
        label: t('Apparato di rete'),
        descrizione: t('Ping, CPU, memoria e le interfacce attive che portano un collegamento documentato o un uplink.'),
      }
    case 'server':
      return {
        label: t('Server'),
        descrizione: t('Ping, CPU, memoria, tutti i volumi, i servizi noti in ascolto e le interfacce attive.'),
      }
    case 'ups':
      return { label: t('Gruppo di continuità'), descrizione: t('Ping, carica della batteria, autonomia e carico.') }
    case 'storage':
      return { label: t('Storage'), descrizione: t('Ping e interfacce attive.') }
    default:
      return { label: t('Base'), descrizione: t('Ping.') }
  }
}

/** «sopra 80 %» / «sotto 20 %»: il verso della soglia conta (una batteria scarica è sotto). */
export function soglia(p: EagleParam, valore: number | null): string {
  if (valore == null) return '—'
  const u = p.unit ? ` ${p.unit}` : ''
  return p.direction === 'below' ? t('sotto {v}', { v: `${valore}${u}` }) : t('sopra {v}', { v: `${valore}${u}` })
}
